import React from 'react';
import PropTypes from 'prop-types';
import ReactPhoneNumberInput, { formatPhoneNumber } from 'react-phone-number-input';
import { reduxFormPropTypes } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { shouldShowError } from 'helpers/page-utils';
import InputLabel from '../InputLabel/InputLabel';
import InputError from '../InputError/InputError';

// This fallback is necessary to run tests in the Jest context, should be temporary
const contextFallback = () => null;
contextFallback.keys = () => [];

// The flag svgs aren't exported directly so we lens in to the folder and require by extension.
// We have a webpack module rule that sees this require, copies the results to our dist, and generates urls.
// We need to get one of those urls and turn it into a template for ReactPhoneNumberInput to use.
// Passing the first key (e.g. `./some/path/US.svg`) to the require.context yields the resolved url (e.g. `studios.cp.com/static/.../US.svg`).
const CountryFlagIconsContext = require?.context?.('country-flag-icons/3x2', false, /\.svg$/) || contextFallback;

const { block, element } = bem('input-field'); // reuses component/Form/Input style

function PhoneNumberInput(props) {
  const { input, meta, defaultCountry, className, showErrorImmediately, label, optional, required, ...restProps } =
    props;
  const hasError = shouldShowError(meta, showErrorImmediately);

  const firstFlag = CountryFlagIconsContext.keys()[0];
  const firstFilename = firstFlag?.split('/')?.slice(-1)[0];
  const flagUrl = CountryFlagIconsContext(firstFlag)?.replace(firstFilename, '{XX}.svg');

  return (
    <div className={block()}>
      <InputLabel message={label} optional={optional} required={required} />
      <ReactPhoneNumberInput
        autoComplete="off" // we only use this in places where people are unlikely to be reentering the same data
        flagUrl={flagUrl} // by default, this points to a dead CDN so we point to our own assets
        className={element('input', { hasError })}
        {...input}
        // this is so that hightlighting the entire response and deleting it will work;
        // for whatever reason, reduxForm's onChange does not get a payload from reactPhoneNumberInput when you highlight
        // the entire selection and then delete it, so the input does not change in redux, even if it's visually removed
        onChange={val => {
          input.onChange(val || '');
        }}
        // the phone number input automatically appends the countrycode to the phone number based on the selected country, but does not display it
        // this is correctly picked up by the validator
        // but onBlur, it removes the countryCode, messing up the number and failing the validator; however, manually passing onBlur preserves it
        // They apparently used to provide their own fix for this problem, but they eliminated it in 3.0 because "redux-form is deprecated"...
        onBlur={() => {
          input.onBlur(formatPhoneNumber(input.value) === '' ? '' : input.value);
        }}
        defaultCountry={defaultCountry}
        {...restProps}
      />
      <InputError {...meta} showErrorImmediately={showErrorImmediately} />
    </div>
  );
}

PhoneNumberInput.defaultProps = {
  showErrorImmediately: false,
};

PhoneNumberInput.propTypes = {
  ...reduxFormPropTypes.field,
  className: PropTypes.string,
  showErrorImmediately: PropTypes.bool,
  countryCodeShort: PropTypes.string,
};

export default PhoneNumberInput;
